import { Search } from '@shared/components/ui/Search';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@shared/components/ui/shadcn/select';
import { useCallback, useEffect, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import type { Courier } from '../../models/shipmentStatusMapping.types';

const SEARCH_DEBOUNCE_MS = 200;

interface FiltersSectionProps {
  couriers: Courier[];
  search: string;
  courierId: string;
  statusId: string;
  onFiltersChange: (updates: { search?: string; courier?: string; status?: string }) => void;
}

export const FiltersSection = ({ couriers, search, courierId, statusId, onFiltersChange }: FiltersSectionProps) => {
  const { t } = useTranslation('shipmentStatusMappingModule');
  const [inputValue, setInputValue] = useState(search);
  const debounceTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const onFiltersChangeRef = useRef(onFiltersChange);

  useEffect(() => {
    onFiltersChangeRef.current = onFiltersChange;
  }, [onFiltersChange]);

  useEffect(() => {
    setInputValue(search);
  }, [search]);

  const emitSearch = useCallback((value: string) => {
    onFiltersChangeRef.current({ search: value.trim() });
  }, []);

  const handleInputChange = useCallback(
    (e: React.ChangeEvent<HTMLInputElement>) => {
      const newValue = e.target.value;
      setInputValue(newValue);

      if (debounceTimerRef.current) {
        clearTimeout(debounceTimerRef.current);
      }

      debounceTimerRef.current = setTimeout(() => {
        emitSearch(newValue);
      }, SEARCH_DEBOUNCE_MS);
    },
    [emitSearch],
  );

  const handleSearchSubmit = useCallback(() => {
    if (debounceTimerRef.current) {
      clearTimeout(debounceTimerRef.current);
      debounceTimerRef.current = null;
    }
    emitSearch(inputValue);
  }, [emitSearch, inputValue]);

  useEffect(() => {
    return () => {
      if (debounceTimerRef.current) {
        clearTimeout(debounceTimerRef.current);
      }
    };
  }, []);

  return (
    <div className="flex min-w-0 flex-1 items-center gap-3">
      <Search
        placeholder={t('shipmentStatusMapping.search.placeholder')}
        value={inputValue}
        onChange={handleInputChange}
        onEnter={handleSearchSubmit}
        dataTestId="shipment-status-search"
        inputDataTestId="shipment-status-search-input"
        wrapperClassName="shrink-0"
      />

      <Select value={courierId} onValueChange={(value) => onFiltersChange({ courier: value })}>
        <SelectTrigger className="h-8 w-40 shrink-0">
          <SelectValue placeholder={t('shipmentStatusMapping.filters.courierPlaceholder')} />
        </SelectTrigger>
        <SelectContent>
          <SelectItem value="all">{t('shipmentStatusMapping.filters.allCouriers')}</SelectItem>
          {couriers.map((courier) => (
            <SelectItem key={courier.id} value={courier.id.toString()}>
              {courier.name}
            </SelectItem>
          ))}
        </SelectContent>
      </Select>

      <Select value={statusId} onValueChange={(value) => onFiltersChange({ status: value })}>
        <SelectTrigger className="h-8 w-36 shrink-0">
          <SelectValue placeholder={t('shipmentStatusMapping.filters.statusPlaceholder')} />
        </SelectTrigger>
        <SelectContent>
          <SelectItem value="all">{t('shipmentStatusMapping.filters.all')}</SelectItem>
          <SelectItem value="mapped">{t('shipmentStatusMapping.filters.mapped')}</SelectItem>
          <SelectItem value="unmapped">{t('shipmentStatusMapping.filters.unmapped')}</SelectItem>
        </SelectContent>
      </Select>
    </div>
  );
};
